// The chat a workspace is worked in, as its walkthrough reads it: what the
// chat claimed about the traces on it (lib/walkClaims.js), live, and the
// chat's messages, for the walkthrough to look into where a claim came from.
// The chat is the workspace's own (its record's `chatId`: the dock tab it was
// paired through, `chat:<local id>`, CanvasChat.jsx useWorkspaceChat), or,
// for a chat's investigation's workspace, the chat its graph key names; read
// whether or not it is the chat in front.
import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { getAgentById, getChatById, getChatByServerId } from "./chatRegistry.js";
import { getDockChatBar, subscribeDock } from "./dockStore.js";
import { graphKeyFor, readLiveGraph, subscribeGraph } from "./investigationGraphRun.js";
import { getCurrentUser } from "./userInfo.js";
import { walkClaims } from "./walkClaims.js";

const NO_CLAIMS = Object.freeze([]);
const given = (v) => v != null && v !== "";
// A chat's local id, from a workspace's record of it (its dock tab's id).
const localIdOf = (chatId) => (given(chatId) ? String(chatId).replace(/^chat:/, "") : null);

// The chat's graph key: its persistent id once it has one, else its local id.
function graphKeyOf({ chatId = null, graphKey = null } = {}) {
    if (given(graphKey)) return String(graphKey);
    const id = localIdOf(chatId);
    if (!given(id)) return null;
    return graphKeyFor(id, getChatById(id)?.serverChatId ?? null);
}

/**
 * What the chat (`chat`: { chatId, graphKey }) claimed, as walkClaims
 * numbers them, live: read again as the chat writes its graph and as the
 * dock's chats change (a new chat's persistent id, which its graph moves to,
 * comes after its first answer). None without a chat.
 */
export function useChatClaims(chat) {
    useSyncExternalStore(subscribeDock, getDockChatBar, () => null);
    const key = graphKeyOf(chat || {});
    const [beat, setBeat] = useState(0);
    useEffect(() => (key ? subscribeGraph(key, () => setBeat((n) => n + 1)) : undefined), [key]);
    return useMemo(() => {
        if (!key) return NO_CLAIMS;
        const claims = walkClaims(readLiveGraph(key));
        return claims.length ? claims : NO_CLAIMS;
    }, [key, beat]); // eslint-disable-line react-hooks/exhaustive-deps
}

/**
 * The chat's messages as its agent holds them: live where it is open in this
 * window, else as this browser kept it; null where neither has it.
 */
export async function readChatMessages({ chatId = null, graphKey = null } = {}) {
    const id = given(chatId) ? localIdOf(chatId) : given(graphKey) ? (getChatByServerId(graphKey)?.id ?? getChatById(graphKey)?.id ?? null) : null;
    if (!given(id)) return null;
    const live = getAgentById(id)?.state?.messages;
    if (Array.isArray(live) && live.length) return live;
    const me = await getCurrentUser().catch(() => null);
    const tenantId = me?.currentTenant?.tenantId ?? null;
    if (!tenantId) return null;
    const [{ loadChat }, { hydrateChatFromStorage }] = await Promise.all([import("./chatStorage.js"), import("./chatSerializer.js")]);
    const record = await loadChat(tenantId, id).catch(() => null);
    return hydrateChatFromStorage(record)?.hydration?.agentMessages || null;
}
